import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AppButton,
  AppTable,
  AppTableCell,
  AppTableHeadCell,
  AppTableRow,
  PageHeader,
  RowNumberCell,
  StatusChip,
} from "../../../../components/ui";
import { Box, IconButton, Tab, Tabs, Tooltip } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import {
  ApplicationRole,
  Category,
  GetUserListRequest,
  UserShortResponse,
  UsersService,
} from "../../../../api";
import { LeadsState } from "../../LeadsManagement/LeadsTables/LeadsTableConstants";
import { TabsHelpers } from "../../../../components/basic/TabsHelpers";
import { toggleUserActive } from "../../Users/shared/userActiveActions";
import { HelpersService } from "../../../../services/Helpers";
import { AttorneysTableFilters } from "./AttorneysFilters/AttorneysTableFilters";
import {
  extractEmailDomains,
  matchesAttorneysFilters,
  resetFilters,
} from "./AttorneysFilters/filtering";
import {
  EMPTY_ATTORNEYS_FILTERS,
  type AttorneysFiltersState,
} from "./AttorneysFilters/types";

const DEFAULT_ROWS_PER_PAGE = 25;
const iconBtnSx = { p: 0.35 };

const ACTIVE_COLUMNS = [
  { id: "name", label: "Name", sortable: true },
  { id: "email", label: "Email", sortable: true },
  { id: "practiceArea", label: "Practice area", sortable: true },
  { id: "leads", label: "Leads", sortable: false },
];

const PENDING_COLUMNS = [
  { id: "name", label: "Name", sortable: true },
  { id: "email", label: "Email", sortable: true },
  { id: "practiceArea", label: "Practice area", sortable: true },
  { id: "status", label: "Status", sortable: false },
];

const INACTIVE_COLUMNS = [
  { id: "name", label: "Name", sortable: true },
  { id: "email", label: "Email", sortable: true },
  { id: "practiceArea", label: "Practice area", sortable: true },
  { id: "status", label: "Status", sortable: false },
];

type Order = "asc" | "desc";
type SortKey = "name" | "email" | "practiceArea";

function getInitials(row: UserShortResponse) {
  const first = row.firstName?.[0] ?? "";
  const last = row.lastName?.[0] ?? "";
  return `${first}${last}`.toUpperCase() || "?";
}

function formatPracticeArea(value?: string | null) {
  return value ? HelpersService.camelToString(value, false) : "—";
}

function getSortValue(row: UserShortResponse, key: SortKey): string {
  switch (key) {
    case "name":
      return `${row.firstName ?? ""} ${row.lastName ?? ""}`.trim().toLowerCase();
    case "email":
      return (row.email ?? "").toLowerCase();
    case "practiceArea":
      return (row.practiceArea ?? "").toLowerCase();
    default:
      return "";
  }
}

function compareAttorneys(
  a: UserShortResponse,
  b: UserShortResponse,
  orderBy: SortKey,
  order: Order
) {
  const result = getSortValue(a, orderBy).localeCompare(
    getSortValue(b, orderBy),
    undefined,
    { sensitivity: "base", numeric: true }
  );
  return order === "asc" ? result : -result;
}

function matchesSearch(row: UserShortResponse, searchTerm: string) {
  const lower = searchTerm.toLowerCase();
  const fullName = `${row.firstName ?? ""} ${row.lastName ?? ""}`.toLowerCase();
  const practice = formatPracticeArea(row.practiceArea).toLowerCase();

  return (
    fullName.includes(lower) ||
    (row.email?.toLowerCase().includes(lower) ?? false) ||
    (row.companyName?.toLowerCase().includes(lower) ?? false) ||
    practice.includes(lower)
  );
}

function totalLeadCount(row: UserShortResponse) {
  return Object.values(row.countByLeadStatus ?? {}).reduce<number>(
    (acc, count) => acc + (count ?? 0),
    0
  );
}

function wonLeadCount(row: UserShortResponse) {
  return (
    (row.countByLeadStatus?.won ?? 0) +
    (row.countByLeadStatus?.signed ?? 0) +
    (row.countByLeadStatus?.paid ?? 0)
  );
}

export default function AttorneysTable() {
  const [value, setValue] = useState(0);
  const [approvedAttorneys, setApprovedAttorneys] = useState<
    UserShortResponse[]
  >([]);
  const [inactiveAttorneys, setInactiveAttorneys] = useState<
    UserShortResponse[]
  >([]);
  const [pendingAttorneys, setPendingAttorneys] = useState<
    UserShortResponse[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [attorneysFilters, setAttorneysFilters] =
    useState<AttorneysFiltersState>(EMPTY_ATTORNEYS_FILTERS);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [orderBy, setOrderBy] = useState<SortKey>("name");
  const [order, setOrder] = useState<Order>("asc");
  const navigate = useNavigate();

  const loadAttorneys = useCallback(() => {
    setLoading(true);
    Promise.all([
      UsersService.postApiUsersGetUsersByRole({
        role: ApplicationRole.ATTORNEY,
      } as GetUserListRequest),
      UsersService.postApiUsersGetUsersByRole({
        role: ApplicationRole.ATTORNEY,
        inactiveOnly: true,
      } as GetUserListRequest),
      UsersService.postApiUsersGetUsersByRole({
        role: ApplicationRole.USER,
        includeInactive: true,
      } as GetUserListRequest),
    ])
      .then(([approved, inactive, users]) => {
        setApprovedAttorneys(approved);
        setInactiveAttorneys(inactive);
        setPendingAttorneys(users.filter((user) => user.isPending));
      })
      .catch(() => {
        setApprovedAttorneys([]);
        setInactiveAttorneys([]);
        setPendingAttorneys([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadAttorneys();
  }, [loadAttorneys]);

  const tabAttorneys =
    value === 0
      ? approvedAttorneys
      : value === 1
        ? pendingAttorneys
        : inactiveAttorneys;

  const columns =
    value === 0
      ? ACTIVE_COLUMNS
      : value === 1
        ? PENDING_COLUMNS
        : INACTIVE_COLUMNS;

  const allAttorneys = useMemo(
    () => [...approvedAttorneys, ...pendingAttorneys, ...inactiveAttorneys],
    [approvedAttorneys, pendingAttorneys, inactiveAttorneys]
  );

  const practiceAreaOptions = useMemo(
    () =>
      Object.values(Category).map((area) => ({
        value: area,
        label: HelpersService.camelToString(area, false),
      })),
    []
  );

  const emailDomainOptions = useMemo(
    () => extractEmailDomains(allAttorneys),
    [allAttorneys]
  );

  const applyAttorneysFilters = (next: AttorneysFiltersState) => {
    setAttorneysFilters(next);
    setPage(0);
  };

  const clearAttorneysFilters = () => {
    applyAttorneysFilters(resetFilters());
  };

  const filteredAttorneys = useMemo(() => {
    let list = tabAttorneys;
    if (searchTerm.trim()) {
      list = list.filter((row) => matchesSearch(row, searchTerm.trim()));
    }
    list = list.filter((row) => matchesAttorneysFilters(row, attorneysFilters));
    return [...list].sort((a, b) => compareAttorneys(a, b, orderBy, order));
  }, [tabAttorneys, searchTerm, attorneysFilters, orderBy, order]);

  const maxPage = Math.max(
    0,
    Math.ceil(filteredAttorneys.length / rowsPerPage) - 1
  );
  const safePage = Math.min(page, maxPage);

  useEffect(() => {
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [page, maxPage]);

  const paginatedAttorneys = useMemo(
    () =>
      filteredAttorneys.slice(
        safePage * rowsPerPage,
        safePage * rowsPerPage + rowsPerPage
      ),
    [filteredAttorneys, safePage, rowsPerPage]
  );

  const handleRequestSort = (columnId: string) => {
    if (columnId !== "name" && columnId !== "email" && columnId !== "practiceArea") {
      return;
    }
    const property = columnId as SortKey;
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0);
  };

  const handleDetailsClick = (id: number) => {
    navigate(`/attorney/${id}`);
  };

  const handleToggleActive = async (
    row: UserShortResponse,
    isActive: boolean
  ) => {
    await toggleUserActive(row.id!, {
      firstName: row.firstName,
      lastName: row.lastName,
      isActive,
      onSuccess: loadAttorneys,
    });
  };

  const goToLeadsFor = (row: UserShortResponse, status?: string) => {
    const attorneyId = row.id;
    const fullName = `${row.firstName} ${row.lastName}`;

    const allNames = approvedAttorneys.map(
      (a) => `${a.firstName} ${a.lastName}`
    );
    const allIds = approvedAttorneys.map((a) => Number(a.id));

    const query = new URLSearchParams({
      attorneyId: String(attorneyId),
    });

    if (status) query.set("attorneyStatus", status);

    const stateData: LeadsState = {
      names: allNames,
      ids: allIds,
      selectedName: fullName,
    };

    navigate(`/attorney-leads?${query.toString()}`, { state: stateData });
  };

  const emptyTitle =
    value === 0
      ? "No active attorneys match the selected filters."
      : value === 1
        ? "No attorneys pending review match the selected filters."
        : "No inactive attorneys match the selected filters.";

  return (
    <Box sx={{ width: "100%" }}>
      <PageHeader
        title="Attorney directory"
        subtitle="Manage active, pending, and inactive attorney accounts."
        actions={
          <>
            <AppButton
              variant="outlined"
              color="primary"
              onClick={() => navigate("/generateLink")}
            >
              Create registration link
            </AppButton>
            <AppButton
              variant="contained"
              color="primary"
              onClick={() => navigate("/attorney/new")}
            >
              Add new attorney
            </AppButton>
          </>
        }
      />

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={value}
          onChange={(_, newValue) => {
            setValue(newValue);
            setPage(0);
          }}
        >
          <Tab
            label={`ACTIVE (${approvedAttorneys.length})`}
            {...TabsHelpers.parseProps(0)}
          />
          <Tab
            label={`PENDING APPROVAL (${pendingAttorneys.length})`}
            {...TabsHelpers.parseProps(1)}
          />
          <Tab
            label={`INACTIVE (${inactiveAttorneys.length})`}
            {...TabsHelpers.parseProps(2)}
          />
        </Tabs>
      </Box>

      <Box sx={{ mb: 2 }}>
        <AttorneysTableFilters
          filters={attorneysFilters}
          searchValue={searchTerm}
          onSearchChange={(next) => {
            setSearchTerm(next);
            setPage(0);
          }}
          onApply={applyAttorneysFilters}
          onClear={clearAttorneysFilters}
          practiceAreaOptions={practiceAreaOptions}
          emailDomainOptions={emailDomainOptions}
        />
      </Box>

      <AppTable
        columns={columns}
        showRowNumber
        sortBy={orderBy}
        sortAsc={order === "asc"}
        onRequestSort={handleRequestSort}
        loading={loading}
        loadingMessage="Loading attorneys..."
        isEmpty={!loading && filteredAttorneys.length === 0}
        emptyTitle={emptyTitle}
        extraColumnCount={1}
        maxHeight="calc(100vh - 340px)"
        trailingHeadCells={
          <AppTableHeadCell align="right" sx={{ width: 100 }}>
            Actions
          </AppTableHeadCell>
        }
        pagination={{
          count: filteredAttorneys.length,
          page: safePage,
          rowsPerPage,
          rowsPerPageOptions: [10, 25, 50, 100],
          onPageChange: setPage,
          onRowsPerPageChange: (next) => {
            setRowsPerPage(next);
            setPage(0);
          },
        }}
      >
        {paginatedAttorneys.map((row, index) => (
          <AppTableRow
            tabIndex={-1}
            key={row.id}
            onClick={() => handleDetailsClick(row.id!)}
            sx={{ cursor: "pointer" }}
          >
            <RowNumberCell value={safePage * rowsPerPage + index + 1} />
            <AppTableCell>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Avatar
                  src={row.photo ?? undefined}
                  alt={`${row.firstName ?? ""} ${row.lastName ?? ""}`.trim()}
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: 13,
                    fontWeight: 700,
                    bgcolor: "primary.main",
                  }}
                >
                  {getInitials(row)}
                </Avatar>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {row.firstName} {row.lastName}
                </Typography>
              </Stack>
            </AppTableCell>
            <AppTableCell>{row.email || "—"}</AppTableCell>
            <AppTableCell>{formatPracticeArea(row.practiceArea)}</AppTableCell>

            {value === 0 ? (
              <AppTableCell
                onClick={(e) => e.stopPropagation()}
                sx={{ py: 1 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.75,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <AppButton
                    variant="contained"
                    color="darkGray"
                    size="small"
                    onClick={() => goToLeadsFor(row)}
                  >
                    All({totalLeadCount(row)})
                  </AppButton>
                  <AppButton
                    variant="contained"
                    color="cyan"
                    size="small"
                    onClick={() => goToLeadsFor(row, "NEW")}
                  >
                    New({row.countByLeadStatus?.new ?? 0})
                  </AppButton>
                  <AppButton
                    variant="contained"
                    color="purple"
                    size="small"
                    onClick={() => goToLeadsFor(row, "ACCEPTED")}
                  >
                    Accepted({row.countByLeadStatus?.accepted ?? 0})
                  </AppButton>
                  <AppButton
                    variant="contained"
                    color="dirtyYellow"
                    size="small"
                    onClick={() => goToLeadsFor(row, "REJECTED")}
                  >
                    Rejected({row.countByLeadStatus?.rejected ?? 0})
                  </AppButton>
                  <AppButton
                    variant="contained"
                    color="brown"
                    size="small"
                    onClick={() => goToLeadsFor(row, "LOST")}
                  >
                    Lost({row.countByLeadStatus?.lost ?? 0})
                  </AppButton>
                  <AppButton
                    variant="contained"
                    color="dirtyBlue"
                    size="small"
                    onClick={() => goToLeadsFor(row, "WON")}
                  >
                    Won({wonLeadCount(row)})
                  </AppButton>
                </Box>
              </AppTableCell>
            ) : (
              <AppTableCell>
                {value === 1 ? (
                  <StatusChip label="Pending" tone="warning" />
                ) : (
                  <StatusChip
                    label="Inactive"
                    tone="neutral"
                    variant="outlined"
                  />
                )}
              </AppTableCell>
            )}

            <AppTableCell
              align="right"
              onClick={(e) => e.stopPropagation()}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.25,
                }}
              >
                <Tooltip title="View details">
                  <IconButton
                    size="small"
                    color="primary"
                    sx={iconBtnSx}
                    onClick={() => handleDetailsClick(row.id!)}
                    aria-label="View attorney details"
                  >
                    <EditOutlinedIcon />
                  </IconButton>
                </Tooltip>
                {value === 0 && (
                  <Tooltip title="Deactivate">
                    <IconButton
                      size="small"
                      color="error"
                      sx={iconBtnSx}
                      onClick={() => handleToggleActive(row, false)}
                      aria-label="Deactivate attorney"
                    >
                      <PersonOffOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {value === 2 && (
                  <Tooltip title="Reactivate">
                    <IconButton
                      size="small"
                      color="primary"
                      sx={iconBtnSx}
                      onClick={() => handleToggleActive(row, true)}
                      aria-label="Reactivate attorney"
                    >
                      <HowToRegOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </AppTableCell>
          </AppTableRow>
        ))}
      </AppTable>
    </Box>
  );
}
